import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { useLoadInfo } from "./useInfo";
import Art from "./Art";
import styled, { keyframes } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { useAppSelector } from "../../store/hooks";
import {
  MINE_ON,
  MINE_PARAM,
  VIEW_CALENDAR,
  VIEW_PARAM,
  filterMine,
  isCalendarView,
  isMineParam,
} from "./galleryFilters";
import GalleryCalendar from "./GalleryCalendar";


const ArtGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  /* margin: 15 auto; */
  padding: 10px;
  align-items: center;
  gap: 5px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    width: 95%;
    margin: 0 auto;
  }
`;

const Title = styled.div`
  font-size: 2rem;
  color: #222;
  text-align: center;
  font-weight: 600;
  padding: 10px;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 10px 6px;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgb(35, 35, 35);
  padding: 8px 16px;
  border: 1.5px solid rgb(35, 35, 35);
  border-radius: 1px;
  cursor: pointer;
  background-color: ${({ $active }) => ($active ? "#60fac1e1" : "#fff")};
  box-shadow: ${({ $active }) => ($active ? "3px 3px rgba(0, 0, 0, 0.6)" : "2px 2px rgba(0, 0, 0, 0.35)")};
  transition-duration: 0.3s;

  &:hover {
    background-color: ${({ $active }) => ($active ? "#60fac1e1" : "#eaeaea")};
    transform: scale(0.98);
  }
`;

const BetaTag = styled.span`
  margin-left: 6px;
  padding: 1px 5px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: 2px;
  color: #fff;
  background: rgb(35, 35, 35);
`;

const flash = keyframes`
  0%, 60% { box-shadow: 0 0 0 4px #60fac1, 6px 6px 0 4px rgba(0, 0, 0, 0.6); }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const reveal = keyframes`
  from { opacity: 0.35; }
  to { opacity: 1; }
`;

const GalleryView = styled.div`
  animation: ${reveal} 0.3s ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

// Opposite the sitebot chip, and clear of the mobile app bar.
const ReturnButton = styled.button`
  position: fixed;
  left: 16px;
  bottom: 44px;
  z-index: 870;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  background: #60fac1;
  color: #232323;
  border: 2px solid #232323;
  border-radius: 1px;
  box-shadow: 4px 4px rgba(0, 0, 0, 0.6);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #a0ffda; }
  &:focus-visible { outline: 3px solid #663399; outline-offset: 4px; }
  @media (max-width: 700px) {
    left: 12px;
    bottom: calc(88px + env(safe-area-inset-bottom, 0px));
  }
`;

const ZoomImage = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 850;
  pointer-events: none;
  transform-origin: top left;
  background-color: #d4d4d4;
  border-radius: 2px;
  will-change: transform, opacity;
`;

type ZoomSource = {
  filename: string;
  rect: { left: number; top: number; width: number; height: number };
  background: React.CSSProperties;
};

function zoomSource(el: HTMLElement | null): ZoomSource | null {
  if (!el?.dataset.f) return null;
  const { left, top, width, height } = el.getBoundingClientRect();
  if (!width || !height) return null;
  const image = el.querySelector<HTMLImageElement>("img");
  const style = getComputedStyle(el);
  return {
    filename: el.dataset.f,
    rect: { left, top, width, height },
    background: image ? {
      backgroundImage: `url("${image.currentSrc || image.src}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    } : {
      backgroundImage: style.backgroundImage,
      backgroundSize: style.backgroundSize,
      backgroundPosition: style.backgroundPosition,
      backgroundRepeat: "no-repeat",
    },
  };
}

// Reserves the card's square before it renders (every AOTD is square), so
// placeholders below the fold are the right height and jumping to a day from
// the calendar lands on it.
const CardSlot = styled.div<{ $flash: boolean }>`
  aspect-ratio: 1 / 1;
  animation: ${({ $flash }) => ($flash ? flash : "none")} 1.8s ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Status = styled.div`
  text-align: center;
  color: #444;
  font-size: 0.95rem;
  padding: 24px 16px;
`;

const Notice = styled.div`
  max-width: 520px;
  margin: 0 auto;
  text-align: center;
  color: #333;
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 14px 16px;
  border: 1.5px solid rgb(35, 35, 35);
  background: #fff8d6;
  box-shadow: 2px 2px rgba(0, 0, 0, 0.35);

  a {
    color: #0a6b4a;
    font-weight: 700;
  }
`;

const EmptyState = styled.div`
  max-width: 520px;
  margin: 0 auto;
  text-align: center;
  color: #333;
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 28px 16px;

  a {
    color: #0a6b4a;
    font-weight: 700;
  }
`;

const LazyArt: React.FC<{ artInfo: any; flash?: boolean }> = ({ artInfo, flash = false }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    rootMargin: '300px 0px',
  });

  return (
    <CardSlot ref={ref} id={`aotd-${artInfo.filename}`} data-f={artInfo.filename} $flash={flash}>
      {inView ? <Art artInfo={artInfo} /> : null}
    </CardSlot>
  );
};


// A few days have two AOTD documents sharing one image file (7 Jun 2026,
// 5 Apr 2025), so the filename alone isn't a unique key.
function withKeys<T extends { filename: string }>(items: T[]) {
  const seen = new Map<string, number>();
  return items.map((artInfo) => {
    const n = (seen.get(artInfo.filename) ?? 0) + 1;
    seen.set(artInfo.filename, n);
    return { key: n === 1 ? artInfo.filename : `${artInfo.filename}#${n}`, artInfo };
  });
}

const ArtGrid: React.FC<{}> = () => {
  const { artInfos, loading, failed } = useLoadInfo();
  const [searchParams, setSearchParams] = useSearchParams();

  const username = useAppSelector((state) => state.auth.username);
  const bootstrapStatus = useAppSelector((state) => state.auth.bootstrapStatus);

  // `bootstrapStatus` only turns "ready" once the silent refresh has settled.
  // Until then a null username means "we don't know yet", not "signed out".
  const authResolved = bootstrapStatus === "ready";
  const signedIn = authResolved && !!username;

  const mineRequested = isMineParam(searchParams.get(MINE_PARAM));
  const mineActive = mineRequested && signedIn;
  // Deep-linked with ?mine=1 while the session is still being restored: hold
  // the grid back instead of flashing everyone's AOTDs and then filtering.
  const holdForAuth = mineRequested && !authResolved;

  const mine = useMemo(
    () => filterMine(artInfos, username),
    [artInfos, username],
  );
  const visible = mineActive ? mine : artInfos;
  const mineSet = useMemo(
    () => (mineActive ? new Set(mine.map((a) => a.filename)) : null),
    [mineActive, mine],
  );

  const calendar = isCalendarView(searchParams.get(VIEW_PARAM));
  const [hasZoomedOut, setHasZoomedOut] = useState(calendar);
  const reduceMotion = useReducedMotion();
  // day to open the calendar on / day to land on when coming back to the grid
  const [anchor, setAnchor] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [flashing, setFlashing] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const pendingZoom = useRef<ZoomSource | null>(null);
  const [flight, setFlight] = useState<{ source: ZoomSource; target: ZoomSource["rect"] } | null>(null);

  useEffect(() => {
    if (calendar) setHasZoomedOut(true);
  }, [calendar]);

  const setView = (view: "grid" | "calendar") => {
    const next = new URLSearchParams(searchParams);
    if (view === "calendar") {
      next.set(VIEW_PARAM, VIEW_CALENDAR);
    } else {
      next.delete(VIEW_PARAM);
    }
    setSearchParams(next, { replace: true });
  };

  const zoomOut = () => {
    // open the calendar on the month of the first card still on screen
    let first: HTMLElement | null = null;
    const cards = gridRef.current?.querySelectorAll<HTMLElement>("[data-f]") ?? [];
    for (let i = 0; i < cards.length; i += 1) {
      const rect = cards[i].getBoundingClientRect();
      if (rect.bottom > 80 && rect.top < window.innerHeight) {
        first = cards[i];
        break;
      }
    }
    pendingZoom.current = reduceMotion ? null : zoomSource(first);
    setAnchor(first?.dataset.f ?? visible[0]?.filename ?? null);
    setHasZoomedOut(true);
    setFocus(null);
    setFlashing(null);
    setView("calendar");
  };

  const pickDay = (filename: string) => {
    const tile = Array.from(document.querySelectorAll<HTMLElement>("[data-testid='aotd-calendar'] button[data-f]"))
      .find((el) => el.dataset.f === filename) ?? null;
    pendingZoom.current = reduceMotion ? null : zoomSource(tile);
    setFocus(filename);
    setView("grid");
  };

  // Back in the grid after picking a day: bring it to the middle and flash it.
  useLayoutEffect(() => {
    if (calendar || !focus) return undefined;
    const el = document.getElementById(`aotd-${focus}`);
    if (!el) return undefined;
    el.scrollIntoView({ block: "center" });
    setFlashing(focus);
    const t = window.setTimeout(() => setFlashing(null), 1900);
    return () => window.clearTimeout(t);
  }, [calendar, focus]);

  // Only one image moves between the two layouts. The calendar itself stays
  // in document flow, so animation never changes pointer or scroll geometry.
  useLayoutEffect(() => {
    const source = pendingZoom.current;
    pendingZoom.current = null;
    if (!source) return;
    const selector = calendar ? "[data-testid='aotd-calendar'] button[data-f]" : "[data-f]";
    const el = Array.from(document.querySelectorAll<HTMLElement>(selector))
      .find((node) => node.dataset.f === source.filename) ?? null;
    const target = zoomSource(el)?.rect;
    if (target) setFlight({ source, target });
  }, [calendar, focus]);

  const setMine = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) {
      next.set(MINE_PARAM, MINE_ON);
    } else {
      next.delete(MINE_PARAM);
    }
    // replace, not push: toggling the filter shouldn't bury the page you came
    // from under a pile of history entries.
    setSearchParams(next, { replace: true });
  };

  const countsReady = !loading && !failed;

  return (
    <div style={{
      // background: 'linear-gradient(to bottom, hsl(0, 81.15942028985505%, 72.94117647058823%), hsl(60, 81.95488721804509%, 73.92156862745098%), hsl(120, 84.72222222222227%, 71.76470588235294%), hsl(180, 82.85714285714285%, 72.54901960784314%), hsl(240, 86.20689655172417%, 71.56862745098039%), hsl(300, 84.39716312056743%, 72.35294117647058%))',
      minHeight: '100vh', // Set the height to fill the screen
      height: 'auto', // Prevent the height from being larger than the screen
    }}>
      <Title>Your AOTD Gallery</Title>

      <FilterBar role="group" aria-label="Filter the gallery">
        <FilterButton
          type="button"
          $active={!mineActive}
          aria-pressed={!mineActive}
          onClick={() => setMine(false)}
        >
          All AOTDs{countsReady ? ` (${artInfos.length})` : ""}
        </FilterButton>
        {signedIn && (
          <FilterButton
            type="button"
            $active={mineActive}
            aria-pressed={mineActive}
            title={`AOTDs claimed by ${username}`}
            onClick={() => setMine(true)}
          >
            Yours{countsReady ? ` (${mine.length})` : ""}
          </FilterButton>
        )}
        <FilterButton
          type="button"
          $active={calendar}
          aria-pressed={calendar}
          title={calendar ? "Back to the big cards" : "See every AOTD at once, month by month"}
          onClick={() => (calendar ? setView("grid") : zoomOut())}
        >
          {calendar ? "Zoom in" : "Zoom out"}
          <BetaTag>beta</BetaTag>
        </FilterButton>
      </FilterBar>

      {/* Asked for "mine" but there is no session to filter by. */}
      {mineRequested && authResolved && !username && (
        <Notice>
          <Link to="/login">Log in</Link> to see the AOTDs you claimed. Showing
          every AOTD for now.
        </Notice>
      )}

      {holdForAuth && <Status>checking your session…</Status>}

      {!holdForAuth && failed && (
        <Notice>Could not load the gallery. Try reloading the page.</Notice>
      )}

      {!holdForAuth && !failed && loading && <Status>loading the gallery…</Status>}

      {!holdForAuth && !failed && !loading && visible.length === 0 && (
        <EmptyState>
          {mineActive ? (
            <>
              No AOTDs under <strong>{username}</strong> yet.
              <br />
              Win the race on <Link to="/art-of-the-day">the Art of the Day</Link>{" "}
              and your day shows up here once it finishes generating.
              <br />
              <br />
              <FilterButton type="button" $active={false} onClick={() => setMine(false)}>
                Show all AOTDs
              </FilterButton>
            </>
          ) : (
            <>No AOTDs to show yet.</>
          )}
        </EmptyState>
      )}

      {!holdForAuth && !failed && visible.length > 0 && (
        <GalleryView key={calendar ? "calendar" : "grid"}>
          {calendar && (
            <GalleryCalendar
              arts={artInfos}
              highlight={mineSet}
              anchorFilename={anchor ?? visible[0]?.filename}
              onPick={pickDay}
            />
          )}

          {!calendar && (
            <ArtGridContainer ref={gridRef}>
              {withKeys(visible).map(({ key, artInfo }) => (
                // Keyed by filename (one per day), not by index: with a filter in
                // play an index key would hand a mounted card a different day.
                <LazyArt key={key} artInfo={artInfo} flash={flashing === artInfo.filename} />
              ))}
            </ArtGridContainer>
          )}
        </GalleryView>
      )}

      {hasZoomedOut && !calendar && !holdForAuth && !failed && visible.length > 0 && (
        <ReturnButton type="button" onClick={zoomOut} aria-label="Zoom out to the calendar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="10" cy="10" r="7" /><path d="M6 10h8m1 5 6 6" />
          </svg>
          Zoom out
        </ReturnButton>
      )}

      {flight && createPortal(
        <ZoomImage
          data-testid="aotd-gallery-zoom-image"
          key={`${flight.source.filename}-${calendar ? "out" : "in"}`}
          aria-hidden="true"
          style={{ ...flight.source.background, width: flight.source.rect.width, height: flight.source.rect.height }}
          initial={{ x: flight.source.rect.left, y: flight.source.rect.top, scaleX: 1, scaleY: 1, opacity: 1 }}
          animate={{
            x: flight.target.left,
            y: flight.target.top,
            scaleX: flight.target.width / flight.source.rect.width,
            scaleY: flight.target.height / flight.source.rect.height,
            opacity: [1, 1, 0],
          }}
          transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1], opacity: { times: [0, 0.75, 1] } }}
          onAnimationComplete={() => setFlight((current) => current === flight ? null : current)}
        />,
        document.body,
      )}
    </div>

  );
};

export default ArtGrid;
